import React, { useState, useRef, useEffect } from 'react';
import './Tooltip.css';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  position?: 'top' | 'right' | 'bottom' | 'left';
  forceVisible?: boolean; // Property to force the tooltip to be visible
}

// Static registry to track active tooltips
const TooltipRegistry = {
  activeTooltips: new Set<string>(),
  registerTooltip: (id: string) => {
    TooltipRegistry.activeTooltips.add(id);
  },
  unregisterTooltip: (id: string) => {
    TooltipRegistry.activeTooltips.delete(id);
  },
  hideOthers: (currentId: string) => {
    TooltipRegistry.activeTooltips.forEach(id => {
      if (id !== currentId) {
        window.dispatchEvent(new CustomEvent('hide-tooltip', { detail: { id } }));
      }
    });
  }
};

const Tooltip: React.FC<TooltipProps> = ({ 
  children, 
  content, 
  position = 'top',
  forceVisible = false
}) => {
  const [isVisible, setIsVisible] = useState(forceVisible);
  const [stayOpen, setStayOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<number | null>(null);
  const tooltipIdRef = useRef<string>(`tooltip-${Math.random().toString(36).substr(2, 9)}`);

  // Update visibility when forceVisible prop changes
  useEffect(() => {
    if (forceVisible) {
      setIsVisible(true);
    }
  }, [forceVisible]);

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current !== null) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    clearHideTimeout();
    if (!isVisible) {
      // Hide other tooltips when showing this one
      TooltipRegistry.hideOthers(tooltipIdRef.current);
      TooltipRegistry.registerTooltip(tooltipIdRef.current);
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    // Don't hide if tooltip is forced visible or has stayOpen flag
    if (forceVisible || stayOpen) return;
    
    clearHideTimeout();
    hideTimeoutRef.current = window.setTimeout(() => {
      setIsVisible(false);
      TooltipRegistry.unregisterTooltip(tooltipIdRef.current);
      hideTimeoutRef.current = null;
    }, 100);
  };

  // Listen for hide-tooltip events
  useEffect(() => {
    const handleHideTooltip = (e: CustomEvent) => {
      if (e.detail.id === tooltipIdRef.current && !stayOpen) {
        clearHideTimeout();
        setIsVisible(false);
        TooltipRegistry.unregisterTooltip(tooltipIdRef.current);
      }
    };

    window.addEventListener('hide-tooltip', handleHideTooltip as EventListener);
    
    return () => {
      window.removeEventListener('hide-tooltip', handleHideTooltip as EventListener);
      clearHideTimeout();
      TooltipRegistry.unregisterTooltip(tooltipIdRef.current);
    };
  }, [stayOpen]);

  // Listen for stayOpen and stayClose events from GuidedTour
  useEffect(() => {
    const container = triggerRef.current;
    if (!container) return;
    
    const handleStayOpen = () => {
      setStayOpen(true);
      setIsVisible(true);
      clearHideTimeout();
    };
    
    const handleStayClose = () => {
      setStayOpen(false);
      setIsVisible(false);
    };
    
    container.addEventListener('stayOpen', handleStayOpen);
    container.addEventListener('stayClose', handleStayClose);
    
    return () => {
      container.removeEventListener('stayOpen', handleStayOpen);
      container.removeEventListener('stayClose', handleStayClose);
    };
  }, []);

  // Check if we're inside a guided tour and keep tooltip visible if needed
  useEffect(() => {
    const isTourActive = document.querySelector('.guided-tour-overlay') !== null;
    
    if (isTourActive && triggerRef.current) {
      // Check if this tooltip or its parent is being highlighted in the tour
      const isHighlighted = triggerRef.current.closest('.guided-tour-spotlight') !== null ||
        document.querySelector('.guided-tour-spotlight') !== null && 
        triggerRef.current.contains(document.querySelector('.guided-tour-spotlight'));
      
      if (isHighlighted) {
        setIsVisible(true);
        setStayOpen(true);
      }
    }
    
    return () => {
      clearHideTimeout();
    };
  }, []);

  // Adjust tooltip position if it goes outside viewport
  useEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight
      };
      
      // Check if tooltip is outside viewport and adjust if needed
      const tooltip = tooltipRef.current.style;
      
      // Default positioning
      if (position === 'top' || position === 'bottom') {
        tooltip.left = `${triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2}px`;
        tooltip.top = position === 'top' 
          ? `${triggerRect.top - tooltipRect.height - 10}px` 
          : `${triggerRect.bottom + 10}px`;
      } else {
        tooltip.top = `${triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2}px`;
        tooltip.left = position === 'left'
          ? `${triggerRect.left - tooltipRect.width - 10}px`
          : `${triggerRect.right + 10}px`;
      }
      
      // Adjust if outside viewport
      const rect = tooltipRef.current.getBoundingClientRect();
      if (rect.left < 0) tooltip.left = '10px';
      if (rect.right > viewport.width) tooltip.left = `${viewport.width - tooltipRect.width - 10}px`;
      if (rect.top < 0) tooltip.top = '10px';
      if (rect.bottom > viewport.height) tooltip.top = `${viewport.height - tooltipRect.height - 10}px`;
    }
  }, [isVisible, position]);
  
  return (
    <div 
      className="tooltip-wrapper tooltip-container" 
      onMouseEnter={handleMouseEnter} 
      onMouseLeave={handleMouseLeave}
      ref={triggerRef}
    >
      {children}
      {isVisible && (
        <div 
          className={`custom-tooltip tooltip-${position}`}
          ref={tooltipRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {content}
        </div>
      )}
    </div>
  );
};

export default Tooltip;
